import React from 'react'

import Row from '@amzn/meridian/row'
import {
    TableCell,
    TableRow,
    TableSectionHeader,
} from '@amzn/meridian/table'
import Text from '@amzn/meridian/text'

import { isZoneSelectMode } from '@components/SideBar/SideBar'
import { sideBarConfig, SideBarMode } from '@utils/ui-config'
import {
    getSubzoneNameToDetailMap,
    getToolNameToDetailMap,
    getToolNameToZoneInfoMap,
    getZoneNameToDetailMap,
    getZoneToSubzoneMap,
} from '@services/ToolMenuConfigService'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { MessageBundle } from '@amzn/arb-tools'

/**
 * Renders the table subcomponents to be populated in sidebar. For
 * zone selection, it renders all subzones as rows with zone name as
 * subheading. For tool selection, it displays the tool list for zone
 * or all tools depending on flag set in sidebar.
 *
 * @param {SideBarMode} sideBarMode Enum value which decides selection menu for tools or zones.
 * @param {boolean} showAllTools Flag to determine if all tools should be displayed.
 * @param {Map} zoneToSubzoneMap Mapping between zones and subzones.
 * @param {Map} subzoneToToolMap Mapping between subzones and their associated tool names.
 * @param {string} zone Current zone selected if any.
 * @param {string} subzone Current subzone selected if any.
 * @param {Function} setZoneCallback Function to set zone, when menu item clicked for zone selection.
 * @param {boolean} isFrequentlyUsedToolsEnabled Flag to determine if the frequently used tools section should be displayed.
 * @returns {TableRow[]} List of rows containing selection items for zone or tool selection.
 */
const getSideBarNavigation = (
    sideBarMode: SideBarMode,
    showAllTools: boolean,
    setShowAllTools: Function,
    zoneToSubzoneMap: Map<string, string[]>,
    subzoneToToolMap: Map<string, string[]>,
    zone: string,
    subzone: string,
    setZoneCallback: (zone: string, subzone: string) => void,
    toggleMenu: () => void,
    toggleMenuButton: () => void,
    t: Function,
    messageBundle: MessageBundle,
    isFrequentlyUsedToolsEnabled: boolean,
    displayNameOverrides?: Map<string, string>,
): React.ReactElement[] => {
    if (isFrequentlyUsedToolsEnabled) {
        return _getAllToolsForFrequentlyUsedToolsLayout(subzoneToToolMap, toggleMenu, toggleMenuButton, t, messageBundle, setZoneCallback, displayNameOverrides)
    } else if (isZoneSelectMode(sideBarMode)) {
        return _getZoneMenuOptions(zoneToSubzoneMap, setZoneCallback, t, messageBundle)
    } else {
        return _getSideBarOptions(showAllTools, setShowAllTools, subzoneToToolMap, zone, subzone, toggleMenu, toggleMenuButton, t, messageBundle, displayNameOverrides)
    }
}

const _getZoneMenuOptions = (
    zoneToSubzoneMap: Map<string, string[]>,
    setZoneCallback: (zone: string, subzone: string) => void,
    t: Function,
    messageBundle: MessageBundle,
): React.ReactElement[] => {
    const zoneOptions = []

    zoneToSubzoneMap = getZoneToSubzoneMap()
    const subzoneNameToDetailMap = getSubzoneNameToDetailMap()
    const zoneNameToDetailMap = getZoneNameToDetailMap()

    // for each zone, subzone - return the subzone as a row with zone as its subheading
    zoneToSubzoneMap.forEach((subzones, zone) => {
        // return zone name as section heading
        const zoneDetail = zoneNameToDetailMap.get(zone)
        zoneOptions.push(
            <TableSectionHeader
                data-atb-id={`sideBar-${zone}`}
                key={`sideBar-${zone}-${Math.floor((Math.random() * 10000) + 1)}`}
            >
                {t(messageBundle, zoneDetail?.translateID) || zoneDetail?.zoneDisplayName || zone}
            </TableSectionHeader>,
        )

        // return zone name as section heading
        const sortedSubzones = subzones.slice(0)
        sortedSubzones.forEach((subzone) => {
            const subzoneDetail = subzoneNameToDetailMap.get(subzone)
            zoneOptions.push(
                <TableRow
                    data-atb-id={`sideBar-${subzone}`}
                    key={`sideBar-${subzone}-${Math.floor((Math.random() * 10000) + 1)}`}
                    onClick={() => setZoneCallback(zone, subzone)}
                >
                    <TableCell>
                        {t(messageBundle, subzoneDetail?.translateID) || subzoneDetail?.subzoneDisplayName || subzone}
                    </TableCell>
                </TableRow>,
            )
        })
    })

    console.log('ATBase: Mapping zone menu options: ', zoneToSubzoneMap)
    return zoneOptions
}

const _getSideBarOptions = (
    showAllTools: boolean,
    setShowAllTools: Function,
    subzoneToToolMap: Map<string, string[]>,
    zone: string,
    subzone: string,
    toggleMenu: () => void,
    toggleMenuButton: () => void,
    t: Function,
    messageBundle: MessageBundle,
    displayNameOverrides?: Map<string, string>,
) => {
    const toolOptions = []
    const toolNameToDetailMap = getToolNameToDetailMap()

    // if showAllTools is set, we want to return all tools regardless of zone
    if (showAllTools) {
        const toolList: string[] = []

        // for each unique tool present in subzone to tool mapping, return a row
        subzoneToToolMap.forEach((tools) => {
            tools.forEach((tool) => {
                // uniqueness check
                if (!toolList.includes(tool)) {
                    toolList.push(tool)
                }
            })
        })

        toolList.forEach((tool) => {
            toolOptions.push(
                <TableRow
                    data-atb-id={`sideBar-${tool}`}
                    key={`sideBar-${tool}-${Math.floor((Math.random() * 10000) + 1)}`}
                    onClick={() => {
                        _navigateToTool(getToolNameToDetailMap().get(tool), toggleMenu, toggleMenuButton)
                    }}
                >
                    <TableCell>
                        {displayNameOverrides?.get(tool) || t(messageBundle, toolNameToDetailMap.get(tool)?.translateID) || toolNameToDetailMap.get(tool)?.displayName || tool}
                    </TableCell>
                </TableRow>,
            )
        })
        return toolOptions
    }

    // for each tool for selected subzone, return a row
    subzoneToToolMap.get(`${zone}.${subzone}`)?.map((tool) => {
        toolOptions.push(
            <TableRow
                data-atb-id={`sideBar-${tool}`}
                key={`sideBar-${tool}-${Math.floor((Math.random() * 10000) + 1)}`}
                onClick={() => {
                    _navigateToTool(getToolNameToDetailMap().get(tool), toggleMenu, toggleMenuButton)
                }}
            >
                <TableCell>
                    {displayNameOverrides?.get(tool) || t(messageBundle, toolNameToDetailMap.get(tool)?.translateID) || toolNameToDetailMap.get(tool)?.displayName || tool}
                </TableCell>
            </TableRow>,
        )
    })

    return toolOptions
}

/**
 * Creates the 'All tools' section of the tool menu. This function is intended
 * to be used when the frequently used tools feature is enabled.
 *
 * @param subzoneToToolMap Maps subzone names to tool names.
 * @param toggleMenu Callback function to toggle menu visibility when a tool is clicked.
 * @param t Translates strings for localization
 * @param setZoneCallback Callback function to set the zone and subzone when a tool is selected.
 * @returns The 'All tools' section of the tool menu when the frequently used tools feature is enabled.
 */
const _getAllToolsForFrequentlyUsedToolsLayout = (
    subzoneToToolMap: Map<string, string[]>,
    toggleMenu: () => void,
    toggleMenuButton: () => void,
    t: Function,
    messageBundle: MessageBundle,
    setZoneCallback: (zone: string, subzone: string) => void,
    displayNameOverrides?: Map<string, string>,
) => {
    const showAllToolsHeaderRow = (
        <TableRow backgroundColor={'secondary'}>
            <TableCell header={true}>
                <Row alignmentHorizontal="justify">
                    <Text type={sideBarConfig.TOOL_LIST_HEADER_SIZE}>
                        {t(messageBundle, 'ihm-associatetoolsbase-all-tools')}
                    </Text>
                </Row>
            </TableCell>
        </TableRow>
    )

    const toolOptions = []

    const toolNameToDetailMap = getToolNameToDetailMap()
    const toolToZoneInfoMap = getToolNameToZoneInfoMap()

    const toolList: string[] = []

    // for each unique tool present in subzone to tool mapping, return a row
    subzoneToToolMap.forEach((tools) => {
        tools.forEach((tool) => {
            // uniqueness check
            if (!toolList.includes(tool)) {
                toolList.push(tool)
            }
        })
    })

    toolList.forEach((tool) => {
        toolOptions.push(
            <TableRow
                data-atb-id={`sideBar-${tool}`}
                key={`sideBar-${tool}-${Math.floor((Math.random() * 10000) + 1)}`}
                onClick={() => {
                    // Get zone / subzone for tool
                    const zoneInfoArray = toolToZoneInfoMap.get(tool)
                    if (zoneInfoArray) {
                        const zoneInfo = zoneInfoArray[0]
                        console.log(`SMP: ${tool} was selected. Updating zone and subzone to ${zoneInfo.zone} - ${zoneInfo.subzone}`)

                        // Set the zone / subzone
                        setZoneCallback(zoneInfo.zone, zoneInfo.subzone)
                    }

                    _navigateToTool(getToolNameToDetailMap().get(tool), toggleMenu, toggleMenuButton)
                }}
            >
                <TableCell>
                    {displayNameOverrides?.get(tool) || t(messageBundle, toolNameToDetailMap.get(tool)?.translateID) || toolNameToDetailMap.get(tool)?.displayName || tool}
                </TableCell>
            </TableRow>,
        )
    })

    return [showAllToolsHeaderRow, ...toolOptions]
}

export default getSideBarNavigation
